import React, {
  useState,
  FunctionComponent,
  ChangeEvent,
  useCallback,
  useEffect,
  ReactElement,
  useMemo,
} from 'react';
import { Input, Typography, MobileInput, Button, TextArea } from '@pwskills/rachnaui';
import { randomId } from '@pwskills/rachnaui/utils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { IEnquiryResponse, IEnquiryType } from '@/interfaces/contactUs';
import consts from '@/utils/contactUs/contactUs.constants';
import ContactUsApi from '@/api/contactUs';
import { isEmail, isName, isValidIndianPhoneNumber } from '@/utils/common/common.validation';
import { IError } from '@/interfaces/common';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import moEvent from '@/utils/moEngage';
import useUserDetails from '../../store/userDetails';

type Props = {
  enquiryTypes: IEnquiryType[];
  setError: (val: string) => void;
  setSuccess: (val: string) => void;
};

type FormData = {
  [key: string]: {
    name: string;
    phoneNumber: string;
    organisation?: string;
    email: string;
    message: string;
    countryCode: string;
    countryCodeISO: string;
  };
};

const Tabs: FunctionComponent<Props> = ({ enquiryTypes, setError, setSuccess }) => {
  const [activeTab, setActiveTab] = useState<string>(enquiryTypes[0]._id);
  const [isError, setIsError] = useState(false);
  // const { user, isLoggedIn } = useContext(UserDetailsContext);
  const { userDetails: user, userIsLoggedIn: isLoggedIn } = useUserDetails();
  const [formData, setFormData] = useState<FormData>({
    '1': {
      name: '',
      email: '',
      phoneNumber: '',
      message: '',
      countryCode: '+91',
      countryCodeISO: 'IN',
    },
    '2': {
      name: '',
      organisation: '',
      email: '',
      phoneNumber: '',
      message: '',
      countryCode: '+91',
      countryCodeISO: 'IN',
    },
  });
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({
    name: '',
    organisation: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const utmQuery = useUTMParameters();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const disabled = useMemo(() => {
    const { name, organisation, email, phoneNumber, message } = formData[activeTab];
    if (activeTab === '1') {
      return (
        name.trim().length < 2 ||
        !email.trim() ||
        !phoneNumber.trim() ||
        !message.trim() ||
        !phoneNumber.trim() ||
        !isValidPhoneNumber
      );
    }
    return (
      name.trim().length < 2 ||
      (organisation && organisation?.trim().length < 2) ||
      !email.trim() ||
      !phoneNumber.trim() ||
      !message.trim() ||
      !phoneNumber.trim() ||
      !isValidPhoneNumber
    );
  }, [formData, activeTab, isValidPhoneNumber]);

  const getFormError = () => {
    const { name, organisation, email, phoneNumber, message, countryCode } = formData[activeTab];
    const errors = {
      name: name.trim().length < 2 ? consts.NAME_ERROR : isName(name) || '',
      organisation:
        activeTab === '2' && organisation && organisation?.trim().length < 2
          ? consts.ORGANISATION_ERROR
          : '',
      email: email.trim() ? isEmail(email.trim()) || '' : consts.EMAIL_ERROR,
      phoneNumber: phoneNumber.trim()
        ? !isValidIndianPhoneNumber(phoneNumber, countryCode)
          ? ''
          : consts.MOBILE_NUMBER_ERROR
        : consts.MOBILE_NUMBER_ERROR,
      message: message.trim() ? '' : consts.MESSAGE_ERROR,
    };

    setInputErrors({
      ...errors,
    });

    return Object.values(errors).some((error) => error !== '');
  };

  useEffect(() => {
    setInputErrors({
      name: '',
      organisation: '',
      email: '',
      phoneNumber: '',
      message: '',
    });
    setIsError(false);
  }, [activeTab]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [activeTab]: {
        ...prevData[activeTab],
        [name]: value,
      },
    }));
    if (inputErrors[name]) {
      setInputErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const handlePhoneNumberChange = (name: string, value: string): void => {
    setFormData((prevData) => ({
      ...prevData,
      [activeTab]: {
        ...prevData[activeTab],
        [name]: value,
      },
    }));
    if (inputErrors[name]) {
      setInputErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return '';
    }

    const token = await executeRecaptcha(randomId());
    return token;
  }, [executeRecaptcha]);

  const clearFormData = (): void => {
    setFormData((prevData) => ({
      ...prevData,
      [activeTab]: {
        ...prevData[activeTab],
        name: '',
        phoneNumber: '',
        organisation: '',
        email: '',
        message: '',
      },
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<boolean> => {
    event.preventDefault();
    if (getFormError()) {
      setIsError(true);
      return false;
    }

    const token = await handleReCaptchaVerify();

    const payload = {
      name: formData[activeTab].name.trim(),
      email: formData[activeTab].email.trim(),
      orgName: formData[activeTab].organisation?.trim(),
      isdCode: formData[activeTab].countryCode.trim(),
      phoneNumber: formData[activeTab].phoneNumber.trim(),
      type: 'corporate',
      enquiryType: 'Cooperate Enquiry',
      message: formData[activeTab].message.trim(),
      source: utmQuery?.utm_source,
      medium: utmQuery?.utm_medium,
      campaign: utmQuery?.utm_campaign,
    };

    if (activeTab === '1') {
      payload.type = 'course';
      payload.enquiryType = 'Course Enquiry';
      delete payload.orgName;
    }

    try {
      const response: IEnquiryResponse = await ContactUsApi.sendEnquiry(payload, token);
      if (isLoggedIn) {
        const moEventData = {
          user_id: user?._id || '',
          phone_number: payload.phoneNumber || '',
          user_type: isLoggedIn ? 'loggedin' : 'not_loggedin',
          course_name: '',
          course_id: '',
          course_status: '',
          enquiry_type: payload.enquiryType, // already added  course_enquiry
          // category_name : ,
          // category_id : ,
          ...(user?.verified && user?.email && { email: user.email }),
        };

        moEvent('course_enquiry_request', moEventData);
      }
      if (response.msg) {
        setSuccess(consts.SUCCESS);
        clearFormData();
      }
      return true;
    } catch (error: unknown) {
      setError((error as IError)?.error || consts.ERROR);
      return false;
    }
  };

  const renderTitle = (): ReactElement[] =>
    enquiryTypes.map(({ _id, name }) => (
      <Typography
        key={_id}
        component="subheading"
        variant="semi-bold"
        onClick={() => {
          setActiveTab(_id);
        }}
        aria-hidden="true"
        className={
          activeTab === _id ? 'text-orange-500 underline underline-offset-8' : 'text-gray-650'
        }
      >
        &nbsp;{name}&nbsp;
      </Typography>
    ));

  const renderInputField = (
    id: string,
    name: string,
    type: string,
    label: string,
    value: string,
    error?: boolean,
    helperText?: string,
    autoFocus?: boolean
  ): ReactElement => (
    <Input
      id={id}
      onBlur={handleChange}
      wrapperClass="mt-6"
      type={type}
      label={label}
      name={name}
      value={value}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      autoFocus={autoFocus}
    />
  );

  const renderContent = (): ReactElement => {
    const tabFormData = formData[activeTab];
    const {
      name,
      phoneNumber,
      organisation = '',
      email = '',
      message,
      countryCodeISO,
    } = tabFormData;

    return (
      <>
        {renderInputField(
          activeTab === '1' ? 'contact_us_course_name' : 'contact_us_corporate_name',
          'name',
          'text',
          consts.FULL_NAME,
          name,
          isError && !!inputErrors.name,
          (isError && inputErrors.name) || '',
          true
        )}
        {activeTab === '2' &&
          renderInputField(
            'contact_us_corporate_organisation',
            'organisation',
            'text',
            consts.ORGANISATION_NAME,
            organisation,
            isError && !!inputErrors.organisation,
            (isError && inputErrors.organisation) || ''
          )}
        {renderInputField(
          activeTab === '1' ? 'contact_us_course_email' : 'contact_us_corporate_email',
          'email',
          'email',
          consts.EMAIL,
          email,
          isError && !!inputErrors.email,
          (isError && inputErrors.email) || ''
        )}
        <MobileInput
          // id={
          //   activeTab === '1' ? 'contact_us_course_phoneNumber' : 'contact_us_corporate_phoneNumber'
          // }
          defaultCountry={countryCodeISO as 'IN'}
          wrapperClass="mt-6"
          countrySearch
          autoFocus={false}
          placeholder="Enter your phone number *"
          onChange={(val) => handlePhoneNumberChange('phoneNumber', val)}
          value={phoneNumber}
          countryCode={(val, countryCodeISOVal = 'IN') => {
            handlePhoneNumberChange('countryCode', val);
            handlePhoneNumberChange('countryCodeISO', countryCodeISOVal);
          }}
          isValid={(val) => setIsValidPhoneNumber(val)}
          error={isError && !!inputErrors.phoneNumber}
          helperText={(isError && inputErrors.phoneNumber) || ''}
        />
        <TextArea
          id={activeTab === '1' ? 'contact_us_course_message' : 'contact_us_corporate_message'}
          wrapperClass="mt-6"
          type="text"
          label={consts.YOUR_MESSAGE}
          name="message"
          value={message}
          onChange={handleChange}
          rows={7}
          error={isError && !!inputErrors.message}
          helperText={isError ? inputErrors.message : ''}
          variant="outlined"
        />
      </>
    );
  };

  return (
    <div>
      <form
        id={activeTab === '1' ? 'contact_us_course_form' : 'contact_us_corporate_form'}
        onSubmit={handleSubmit}
        className="flex flex-col large:!justify-between regular:!h-[560px]"
      >
        <div>
          <div className="flex justify-center regular:!justify-start gap-6 cursor-pointer">
            {renderTitle()}
          </div>
          <div>{renderContent()}</div>
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={disabled}
          className="self-center regular:!self-start !mt-12 large:!mt-0"
        >
          {consts.SUBMIT}
        </Button>
      </form>
    </div>
  );
};
export default Tabs;
